import React from 'react';
import { styled } from '@compiled/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import DropdownMenu, { DropdownItemRadio, DropdownItemRadioGroup } from '@atlaskit/dropdown-menu';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const MoreLozenge = ({ categories, defaultOpen = false }: Props) => {
	const { formatNumber, formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<DropdownMenu
			defaultOpen={defaultOpen}
			trigger={({ triggerRef, ...otherProps }) => {
				const { isSelected: isDropdownOpen } = otherProps;
				return (
					<LozengeButton
						{...otherProps}
						aria-haspopup="true"
						isDropdownOpen={isDropdownOpen ?? false}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						ref={triggerRef as React.LegacyRef<HTMLButtonElement>}
						isSelected={categories.some((category) => category.isSelected)}
						role="tab"
						data-testid="software-releases-version-related-work.ui.category-filter.more-lozenge.dropdown-trigger"
					>
						{formatMessage(messages.moreButtonLabel, { count: categories.length })}
					</LozengeButton>
				);
			}}
		>
			<DropdownItemRadioGroup id="categories">
				{categories.map(({ label, count, onClick, isSelected }) => (
					<DropdownItemRadio
						isSelected={isSelected}
						onClick={() => {
							onClick();
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'lozenge',
								}),
								'relatedWorkCategory',
							);
						}}
						id={label}
						key={label}
						testId="software-releases-version-related-work.ui.category-filter.more-lozenge.dropdown-item-button"
					>
						{label} ({formatNumber(count)})
					</DropdownItemRadio>
				))}
			</DropdownItemRadioGroup>
		</DropdownMenu>
	);
};

interface ButtonProps {
	isSelected: boolean;
	isDropdownOpen?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LozengeButton = styled.button<ButtonProps>({
	display: 'flex',
	borderRadius: '14px',
	borderStyle: 'none',
	paddingTop: token('space.050'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.100'),
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isSelected, isDropdownOpen }: ButtonProps) => {
		// Nested ternary operator is needed, otherwise there's a bug that the first token is applied regardless of condition
		// eslint-disable-next-line no-nested-ternary
		return isDropdownOpen
			? token('color.background.neutral.bold')
			: isSelected
				? token('color.background.selected') // RLSS-2812 Replace color.background.neutral to color.background.accent.gray.subtlest
				: token('color.background.neutral');
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isSelected, isDropdownOpen }: ButtonProps) => {
		// Nested ternary operator is needed, otherwise there's a bug that the first token is applied regardless of condition
		// eslint-disable-next-line no-nested-ternary
		return isDropdownOpen
			? token('color.text.inverse')
			: isSelected
				? token('color.text.information')
				: token('color.text.subtlest');
	},
	font: token('font.body.UNSAFE_small'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '18px',
});
